import React, {
  useState,
  useEffect,
  type ChangeEvent,
  useRef,
  useContext,
} from 'react';
import { TextField, TextFieldHelperText } from '@rmwc/textfield';
import classNames from 'classnames';
import { type RootState } from '../../store'; // Adjust according to your store setup
import { updateField } from '../../actions/actionCreators';
import errorMessage from '../../utils/errorMessage';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { FieldsetValidatorContext } from './Form';
import { validators } from '../../utils/validators';

interface TextFieldWrapperProps {
  name: string;
  label?: string;
  required?: boolean;
  form: string;
  textarea?: boolean;
  rows?: number | string;
  fullWidth?: boolean;
  heading?: string;
  className?: string;
  placeholder?: string;
  type?: string;
  maxLength?: number;
  hidden?: boolean;
  column?: boolean;
  preferences?: boolean;
  validator?: string;
}

const TextFieldWrapper: React.FC<TextFieldWrapperProps> = ({
  name,
  label,
  required,
  form,
  textarea,
  rows,
  fullWidth,
  heading,
  className,
  placeholder,
  type,
  maxLength,
  hidden,
  column,
  preferences,
}) => {
  const el = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const value = useAppSelector(
    (state: RootState) => state.forms[form]?.[name]?.value || ''
  );
  const [error, setError] = useState<string | null>(null);
  const [displayError, setDisplayError] = useState(false);
  const fieldsetValidator = useContext(FieldsetValidatorContext);

  const validate = () => {
    const errorMessages = errorMessage(
      {
        name,
        label,
        required,
        el: el.current,
      },
      validators.required(value)
    );

    setError(errorMessages || null);
    return !errorMessages;
  };

  useEffect(() => {
    let valid = validate();

    if (el.current) {
      fieldsetValidator.register({
        name,
        required,
        el: el.current,
        valid,
      });
    }

    return () => fieldsetValidator.unregister(name);
  }, [el, value]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = e.target.value;
    dispatch(updateField(form, name, newValue));

    setDisplayError(true);
  };

  const placeholderText = preferences
    ? // eslint-disable-next-line max-len
      'For example\nI need a translator\nI have a hearing impairment\nI prefer email communication\nI can’t answer the phone at certain times.'
    : placeholder;

  return (
    <>
      {heading && <h3 className="step__heading mt--0">{heading}</h3>}
      <div
        className={classNames(className, 'form__fieldholder', {
          'form__fieldholder--error': error,
          'form__fieldholder--hasValue': value,
          'form__fieldholder--hidden': hidden,
          'form__fieldholder--column': column,
          'form__fieldholder--fullWidth': fullWidth,
        })}
        ref={el}
      >
        <TextField
          type={type}
          name={name}
          label={label}
          id={name}
          value={value}
          onChange={handleChange}
          onBlur={() => {
            setDisplayError(true);
            validate();
          }}
          required={required}
          textarea={textarea}
          placeholder={placeholderText}
          rows={typeof rows === 'string' ? parseInt(rows, 10) : rows}
          fullwidth={fullWidth}
          className={className}
          maxLength={maxLength}
        />
        {displayError && error && (
          <>
            <TextFieldHelperText persistent validationMsg>
              {error}
            </TextFieldHelperText>
            <div className="validation-icon validation-wrong"></div>
          </>
        )}
        {value.length > 0 && !error && (
          <div className="validation-icon validation-ok"></div>
        )}
      </div>
    </>
  );
};

export default TextFieldWrapper;
